import { routes } from 'vue-router/auto-routes'
import { setupLayouts } from 'virtual:generated-layouts'
import { createApp } from 'vue'

import { createRouter, createWebHistory } from 'vue-router'
import { VueFire, VueFireAuthWithDependencies } from 'vuefire'
import { browserPopupRedirectResolver, indexedDBLocalPersistence } from 'firebase/auth'
import { initializeApp } from 'firebase/app'
import { createHead } from '@unhead/vue/client'
import type { EmployerRole, PermissionLevel, PermissionResource } from '@swipe4work/api-client-fetch'
import { VueQueryPlugin } from '@tanstack/vue-query'
import App from './App.vue'
import 'virtual:uno.css'

import type { UserModule } from './types'
import chatHubService from '@/modules/signalr-hubs/chat-hub'
import coordinatorHubService from '@/modules/signalr-hubs/coordinator-hub'
import Vue3LottiePlugin from '@/modules/vue3lottie'
import type { AuthProvider } from '@/services/auth/auth-service'
import pinia from '@/modules/pinia'
import apiClients from '@/modules/api-clients/api-clients'
import clickAsync from '@/directives/click-async'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_APP_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_APP_FIREBASE_MESSAGE_SENDER_ID,
  appId: import.meta.env.VITE_APP_FIREBASE_APP_ID,
}

declare module 'vue-router' {
  interface RouteMeta {
    allowAnonymous?: boolean
    allowedRoles?: EmployerRole[]
    permissionResource?: PermissionResource
    permissionLevel?: PermissionLevel
    requiredAuthProvider?: AuthProvider

    layout?: string | boolean
  }
}

function init() {
  const app = createApp(App)

  // directives
  app.directive('click-async', clickAsync)

  app.use(chatHubService)
  app.use(coordinatorHubService)
  app.use(apiClients)

  app.use(createHead())
  app.use(Vue3LottiePlugin)

  app.use(VueFire, {
    // imported above but could also just be created here
    firebaseApp: initializeApp(firebaseConfig),
    modules: [
      VueFireAuthWithDependencies({
        dependencies: {
          popupRedirectResolver: browserPopupRedirectResolver,
          persistence: indexedDBLocalPersistence,
        },
      }),
    ],
  })

  const router = createRouter({
    routes: setupLayouts(routes),
    history: createWebHistory(import.meta.env.BASE_URL),
  })

  app.use(router)

  app.use(pinia)

  app.use(VueQueryPlugin, {
    enableDevtoolsV6Plugin: true,
    queryClientConfig: {
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    },
  })

  // Install modules in modules/ folder
  Object.values(import.meta.glob<{ install: UserModule }>('./modules/*.ts', { eager: true })).forEach(i => i.install?.(app, router))
  app.mount('#app')
}

// Track preload error reloads with a limit of 3
window.addEventListener('vite:preloadError', (error) => {
  console.info('Vite preload error:', error)

  // Get current reload count from localStorage
  const reloadCount = parseInt(localStorage.getItem('vitePreloadErrorReloadCount') || '0', 10)

  if (reloadCount < 3) {
    // Increment and save the reload count
    localStorage.setItem('vitePreloadErrorReloadCount', String(reloadCount + 1))
    window.location.reload()
  } else {
    console.error('Vite preload error persists after 3 reload attempts')
    // Reset the counter for future sessions
    localStorage.setItem('vitePreloadErrorReloadCount', '0')
  }
})

if (document.readyState === 'loading')
  document.addEventListener('DOMContentLoaded', init)
else
  init()
